import axios from 'axios';
import { create } from 'zustand';
import { makeId } from '../PolicyAdmin/utils';
import { DEFAULT_BORDEREAU_SETTINGS } from './constants';
import { reportingService } from './reportingService';
import type {
  BordereauSettings,
  BordereauType,
  ExportHistoryEntry,
  PolicyReportRecord,
  ReportingFilters,
  ReportingReferenceData,
} from './types';
import { resolvePeriod, todayStr, type PeriodPresetKey } from './utils';

const DEFAULT_PRESET: PeriodPresetKey = 'last_12m';

function defaultFilters(): ReportingFilters {
  const { fromDate, toDate } = resolvePeriod(DEFAULT_PRESET);
  return { fromDate, toDate, products: [], carrierIds: [], brokerIds: [], statuses: [] };
}

/** Pull a human-readable message out of an API error (DRF `detail`, field errors, or a string body). */
function extractMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      if (typeof record.detail === 'string') return record.detail;
      if (typeof record.message === 'string') return record.message;
    }
  }
  return fallback;
}

interface ReportingState {
  records: PolicyReportRecord[];
  generatedAt: string | null;
  loading: boolean;
  loaded: boolean;
  error: string | null;

  filters: ReportingFilters;
  periodPreset: PeriodPresetKey;

  reference: ReportingReferenceData;
  referenceLoaded: boolean;

  bordereauType: BordereauType;
  bordereauSettings: BordereauSettings;
  exportHistory: ExportHistoryEntry[];

  fetchRecords: () => Promise<void>;
  fetchReferenceData: (force?: boolean) => Promise<void>;

  setPeriodPreset: (preset: PeriodPresetKey) => void;
  setFilters: (patch: Partial<ReportingFilters>) => void;
  clearFilters: () => void;

  setBordereauType: (type: BordereauType) => void;
  setBordereauSettings: (patch: Partial<BordereauSettings>) => void;
  recordExport: (entry: Omit<ExportHistoryEntry, 'id' | 'generatedAt'>) => void;
}

export const useReportingStore = create<ReportingState>((set, get) => ({
  records: [],
  generatedAt: null,
  loading: false,
  loaded: false,
  error: null,

  filters: defaultFilters(),
  periodPreset: DEFAULT_PRESET,

  reference: { products: [], carriers: [], brokers: [] },
  referenceLoaded: false,

  bordereauType: 'combined',
  bordereauSettings: { ...DEFAULT_BORDEREAU_SETTINGS, periodEnd: defaultFilters().toDate },
  exportHistory: [],

  async fetchRecords() {
    set({ loading: true, error: null });
    try {
      const response = await reportingService.fetchPolicyRecords(get().filters);
      set({
        records: response.records,
        generatedAt: response.generatedAt,
        loading: false,
        loaded: true,
      });
    } catch (err) {
      set({
        loading: false,
        loaded: true,
        error: extractMessage(err, 'Could not load reporting data. Try again in a moment.'),
      });
    }
  },

  async fetchReferenceData(force = false) {
    if (get().referenceLoaded && !force) return;
    try {
      const reference = await reportingService.fetchReferenceData();
      set({ reference, referenceLoaded: true });
    } catch {
      // Filter options are a convenience — the page still works without them.
      set({ referenceLoaded: true });
    }
  },

  setPeriodPreset(preset) {
    if (preset === 'custom') {
      set({ periodPreset: preset });
      return;
    }
    const { fromDate, toDate } = resolvePeriod(preset);
    set(state => ({
      periodPreset: preset,
      filters: { ...state.filters, fromDate, toDate },
      // Keep the bordereau period aligned with what the user is looking at.
      bordereauSettings: { ...state.bordereauSettings, periodEnd: toDate },
    }));
    void get().fetchRecords();
  },

  setFilters(patch) {
    const changesRange = patch.fromDate !== undefined || patch.toDate !== undefined;
    set(state => ({
      filters: { ...state.filters, ...patch },
      periodPreset: changesRange ? 'custom' : state.periodPreset,
      bordereauSettings: patch.toDate
        ? { ...state.bordereauSettings, periodEnd: patch.toDate }
        : state.bordereauSettings,
    }));
    void get().fetchRecords();
  },

  clearFilters() {
    const filters = defaultFilters();
    set(state => ({
      filters,
      periodPreset: DEFAULT_PRESET,
      bordereauSettings: { ...state.bordereauSettings, periodEnd: filters.toDate },
    }));
    void get().fetchRecords();
  },

  setBordereauType(type) {
    set({ bordereauType: type });
  },

  setBordereauSettings(patch) {
    set(state => ({ bordereauSettings: { ...state.bordereauSettings, ...patch } }));
  },

  recordExport(entry) {
    set(state => ({
      exportHistory: [
        { ...entry, id: makeId('bdx'), generatedAt: new Date().toISOString() },
        ...state.exportHistory,
      ].slice(0, 10),
    }));
  },
}));

/** Exposed for tests and for the "data as at" label. */
export const reportingToday = todayStr;
